import {useEffect, useState} from 'react';
import {useSelector} from 'react-redux';
import {selectGameType, selectPlayers} from "../../redux/reducers/sharedStateReducer/selectors";
import {selectAuthState} from "../../redux/reducers/privateStateReducer/selectors";
import {getCityCaptureAnimationRemainingMs} from "../../utils/phase/turnTimeLimits";
import {GAME_TYPES} from "../../constants/game";
import store from "../../redux/store";
import {addNetworkMessageSubscriber, getLastBotWorkDelegation, getNetworkSocket} from '../../utils/networkSocket';
import {executeBotStep} from "../../utils/bots/executeBotStep";
import {getBotWorkKey} from "../../utils/bots/online/getBotWorkKey";
import {
  bindDelegatedBotWork,
  DELEGATED_WORK_DROP_REASONS,
  getDelegatedBotWorkAfterRevoke,
  getDelegatedBotWorkTickDecision,
} from "../../utils/bots/online/delegatedBotWork";
import {shouldUseBotStrongMode} from "../../utils/bots/botConfig";
import {getOnlineBotActionDelayMs} from "../../utils/bots/online/botPacing";
import {resolveBotConstructionTurnPacing} from "../../utils/bots/online/constructionTurnPacing";
import {createBotWorkStepper} from "../../utils/bots/online/botWorkStepper";
import {runInBotWorkScope} from "../../utils/bots/online/botWorkScope";
import {REGISTRY_SINGLETON} from "../../scenes/Registry";
import {selectPhase} from "../../redux/reducers/sharedStateReducer/selectors";
import {PHASES} from "../../constants/phases";

/**
 * Тик шага бота. В фазе строительства сцена перерисовывается один раз на тик,
 * а не на каждый диспатч шага: на телефоне делегата каждая синхронная
 * перерисовка (пересборка поля, зона кварталов, доступность строительства)
 * стоила заметную долю фриза, а увидеть промежуточные состояния одной задачи
 * все равно нельзя. В «спланированном» ходе все строения ставятся за один
 * тик — там разница больше всего. В фазе войны не откладываем: перерисовки там
 * считают разницу позиций отрядов для анимаций. Зарегистрированной может быть
 * и не сцена (объект игры до инициализации сцены) — тогда шаг как есть
 */
const runStepperTick = (stepper) => {
  const scene = REGISTRY_SINGLETON.getGameScene();
  const isConstructionTick = selectPhase(store.getState()) === PHASES.CONSTRUCTION;
  if (isConstructionTick && typeof scene?.runWithDeferredStoreUpdates === 'function') {
    return scene.runWithDeferredStoreUpdates(() => stepper.step());
  }
  return stepper.step();
};

/**
 * Исполнитель делегированных ходов ботов в сетевой игре.
 *
 * Сервер на каждую «работу бота» (его ход либо его подтверждения этапа ближнего
 * боя) выбирает случайного подключенного реального игрока и присылает его клиенту
 * DELEGATE_BOT_WORK с ключом работы. Пока ключ актуального состояния совпадает с
 * делегированным, компонент незаметно для пользователя выполняет шаги бота тем же
 * движком, что и в локальной игре; экшены уходят на сервер обычным каналом
 * синхронизации (networkMiddleware) и ретранслируются остальным игрокам.
 *
 * Работа привязана к сокету, который ее доставил (см. delegatedBotWork): после
 * переподключения прежняя работа не исполняется — сервер ее либо уже отдал
 * другому, либо пришлет заново в новый сокет.
 *
 * Пользователю ничего не отображается; игроки с ролью админа получают короткое
 * логирование делегирования и экшенов бота в консоль.
 */
const OnlineBotRunner = () => {
  const gameType = useSelector(selectGameType);
  const auth = useSelector(selectAuthState);
  const isAdminUser = Boolean(auth?.isAdmin || auth?.isSuperAdmin);

  const [delegatedWork, setDelegatedWork] = useState(null);

  // Подписка на сообщения делегирования (см. networkSocket): к объекту сокета
  // не привязана, поэтому переподключение ее не рвет — раньше ради этого
  // приходилось переустанавливать подмену ws.onmessage на каждый реконнект
  useEffect(() => {
    if (gameType !== GAME_TYPES.ONLINE) return undefined;

    // Работа могла прийти раньше, чем компонент подписался: на свежей странице
    // сервер шлет ее сразу за LOAD_STATE, а экран матча с этим компонентом
    // отрисовывается позже. Сокет ее запоминает (см. networkSocket), годность
    // проверит первый же шаг
    const lastDelegation = getLastBotWorkDelegation();
    if (lastDelegation) {
      setDelegatedWork((current) => current || bindDelegatedBotWork(lastDelegation.payload, lastDelegation.socket));
    }

    return addNetworkMessageSubscriber((msg, socket) => {
      // Сервер отозвал работу раньше смены хода: админ перехватил управление
      // ботом или вернул его. Работу пересчитают и, если нужно, выдадут заново
      // уже с актуальным списком ботов под ручным управлением
      if (msg.type === 'REVOKE_BOT_WORK') {
        setDelegatedWork((current) => getDelegatedBotWorkAfterRevoke(current, msg.payload));
        return;
      }
      if (msg.type !== 'DELEGATE_BOT_WORK' || !msg.payload?.workKey) return;
      if (isAdminUser) {
        console.log(
          `[BotDelegate] Сервер делегировал этому клиенту работу бота «${msg.payload.botName || msg.payload.botPlayerId}» (${msg.payload.workKey})`
        );
      }
      // Новая работа всегда заменяет прежнюю и привязывается к доставившему ее
      // сокету — даже с тем же ключом: так сервер возвращает работу после
      // переподключения. Без доставившего сокета работа не исполняется
      setDelegatedWork(bindDelegatedBotWork(msg.payload, socket));
    });
  }, [gameType, isAdminUser]);

  // Цикл шагов бота, пока делегированная работа актуальна
  useEffect(() => {
    if (gameType !== GAME_TYPES.ONLINE || !delegatedWork) return;

    // Множитель пауз этого бота на весь матч — назначен сервером при старте
    // (см. assignBotSpeedFactors), чтобы боты не ходили в одном ритме.
    // «Явный» бот из этого правила выведен: он помечен ботом для всех игроков,
    // прятать его ритм не от кого — ходит ровно (см. visibleRankedBots)
    const speedFactor = delegatedWork.speedFactor || 1;
    const checkIfVisibleBot = () => Boolean(
      selectPlayers(store.getState()).find((player) => player.id === delegatedWork.botPlayerId)?.isVisibleBot
    );
    const getBotDelayMs = () => getOnlineBotActionDelayMs({
      isVisibleBot: checkIfVisibleBot(),
      speedFactor,
    });

    let cancelled = false;
    let timerId = null;

    // Обертка store исполнителя: каждый диспатч бота идет в области работы
    // бота — блокировка ввода самого игрока (время его хода вышло) действий
    // бота не касается (см. botWorkScope). Обернут именно диспатч, а не только
    // шаг: перемещение бота пишется в стор позже, в конце полета отряда.
    // Админу — еще и логирование экшенов, выполняемых за бота
    const executionStore = {
      getState: () => store.getState(),
      dispatch: (action) => {
        if (isAdminUser) {
          console.log(`[BotDelegate] ${delegatedWork.botName || delegatedWork.botPlayerId}: ${action.type}`);
        }
        return runInBotWorkScope(() => store.dispatch(action));
      },
    };

    const runStep = ({phaseCompletion, holdPolicyPurchase} = {}) => {
      // Сильный режим включен для сетевых ботов по умолчанию, кроме фракций,
      // которым он по данным симуляций вредит (BOT_STRONG_MODE_DISABLED_FACTIONS,
      // developer_artifacts/factions_rules.md) — обычный режим также сохранен
      // для симуляций (server/scripts/simulateBotBatch.js)
      const delegatedBotPlayer = selectPlayers(store.getState())
        .find((player) => player.id === delegatedWork.botPlayerId);
      return executeBotStep(executionStore, {
        online: true,
        strongMode: shouldUseBotStrongMode(delegatedBotPlayer?.factionId),
        // Боты, которыми вручную управляет онлайн-админ: за них не ходим
        controlledBotIds: delegatedWork.controlledBotIds || [],
        phaseCompletion,
        holdPolicyPurchase,
      });
    };

    // Темп хода строительства: «планировал» ли бот этот ход (бросок сервера
    // на ход, см. constructionTurnPacing). Решается сразу, как пришла работа.
    // Если состояние еще не показывает ход этого бота — обычный темп
    const stepper = createBotWorkStepper({
      pacing: resolveBotConstructionTurnPacing(store.getState(), delegatedWork.botPlayerId, delegatedWork.pacingRoll),
      runStep,
      checkIfWorkActual: () => getBotWorkKey(store.getState()) === delegatedWork.workKey,
      getActionDelayMs: getBotDelayMs,
    });

    const tick = () => {
      if (cancelled) return;

      const decision = getDelegatedBotWorkTickDecision(delegatedWork, {
        currentSocket: getNetworkSocket(),
        currentWorkKey: getBotWorkKey(store.getState()),
      });
      if (!decision.execute) {
        // Работа закрыта: ход/этап сменился, либо сокет, получивший работу,
        // закрылся или сменился (переподключение). Следующего делегата
        // назначит сервер. Снимаем только эту работу: пока шаг ждал своего
        // таймера, могла прийти новая — в том числе с тем же ключом, в новый
        // сокет, — ее не трогаем
        if (isAdminUser && decision.reason !== DELEGATED_WORK_DROP_REASONS.WORK_KEY_CHANGED) {
          console.log(`[BotDelegate] ${delegatedWork.botName || delegatedWork.botPlayerId}: работа снята (${decision.reason})`);
        }
        setDelegatedWork((current) => (current === delegatedWork ? null : current));
        return;
      }

      // Пока у игроков идет облет захваченных городов, бот ждет его конца:
      // иначе его ходы шли бы за кадром (то же правило у сервера)
      // Поправка на серверное время — мягко: состояния, собранные вручную
      // (тесты), бывают без нее
      const currentState = store.getState();
      const captureAnimationRemainingMs = getCityCaptureAnimationRemainingMs(
        currentState?.shared?.gameState,
        Date.now() + (currentState?.private?.serverTime?.serverTimeDelta || 0)
      );
      if (captureAnimationRemainingMs > 0) {
        timerId = setTimeout(tick, captureAnimationRemainingMs);
        return;
      }

      let nextDelayMs;
      try {
        // Весь шаг — в области работы бота: проверки блокировки ввода игрока
        // на входах (handleCompleteTurn) шаг бота не останавливают
        nextDelayMs = runInBotWorkScope(() => runStepperTick(stepper));
      } catch (error) {
        console.error('[BotDelegate] step failed', error);
        nextDelayMs = getBotDelayMs();
      }

      timerId = setTimeout(tick, nextDelayMs);
    };

    // Пауза перед первым шагом такая же, как между остальными: раньше фаза
    // снабжения начиналась мгновенно (шаг там один), и живые игроки вычисляли
    // ботов по тому, что снабжение у них заканчивалось без задержки.
    // Исключение — «спланированный» ход строительства: план живого игрока
    // тоже ставится без паузы
    timerId = setTimeout(tick, stepper.getFirstDelayMs());

    return () => {
      cancelled = true;
      clearTimeout(timerId);
    };
  }, [gameType, delegatedWork, isAdminUser]);

  return null;
};

export default OnlineBotRunner;
